import {
  AcceptOfficialAcceptCollectionRequest,
  AcceptOfficialAcceptCollectionRequestToJSON,
  BookbindingCollection,
  BookbindingCollectionFromJSON,
  BookbindingCollectionsRequest,
  BookbindingCollectionsRequestToJSON,
  BookbindingTargetCollection,
  BookbindingTargetCollectionFromJSON,
  BookbindingTargetMagazineCollection,
  BookbindingTargetMagazineCollectionFromJSON,
  BulkUpdateCollection,
  BulkUpdateCollectionFromJSON,
  BulkUpdateCollectionsRequest,
  BulkUpdateCollectionsRequestToJSON,
  CheckBulkUpdateCollectionsRequest,
  CheckBulkUpdateCollectionsRequestToJSON,
  CheckOfficialAcceptCollectionRequest,
  CheckOfficialAcceptCollectionRequestToJSON,
  Collection,
  CollectionDeleteRequest,
  CollectionDeleteRequestToJSON,
  CollectionFromJSON,
  CollectionLendCount,
  CollectionLendCountFromJSON,
  CollectionMemo,
  CollectionMemoFromJSON,
  CollectionRemoveRequest,
  CollectionRemoveRequestToJSON,
  exists,
  GetBibliographyFullIdRequest,
  GetBibliographyFullIdRequestToJSON,
  GetBookbindingTargetMagazineCollectionsRequest,
  GetBookbindingTargetMagazineCollectionsRequestToJSON,
  GetCollectionLendCountRequest,
  GetCollectionLendCountRequestToJSON,
  GetBookbindingCollectionRequest,
  GetBookbindingCollectionRequestToJSON,
  GetBookbindingTargetCollectionsRequest,
  GetBookBoundCollectionsRequest,
  GetOfficialAcceptCollectionRequest,
  GetOfficialAcceptCollectionRequestToJSON,
  GetSimpleCollectionRequest,
  GetSimpleCollectionRequestToJSON,
  HTTPBody,
  HTTPQuery,
  OfficialAcceptCollection,
  OfficialAcceptCollectionFromJSON,
  ReturnHistory,
  ReturnHistoryFromJSON,
  SimpleCollection,
  SimpleCollectionFromJSON,
  UpdateCollectionMemoRequest,
  UpdateCollectionMemoRequestToJSON,
  UpdateCollectionRequest,
  UpdateCollectionRequestToJSON,
  GetBookbindingTargetCollectionsRequestToJSON,
  GetBookBoundCollectionsRequestToJSON,
  FixShelfTypeTempChangeRequest,
  FixShelfTypeTempChangeRequestToJSON,
  GetShelfTypeTempChangeFixCollectionsRequestToJSON,
  GetShelfTypeTempChangeFixCollectionsRequest,
  GetShelfTypeTempChangeCollectionRequest,
  GetShelfTypeTempChangeCollectionRequestToJSON,
  ShelfTypeTempChangeCollection,
  ChangeShelfTypeTempRequest,
  ChangeShelfTypeTempRequestToJSON,
  ShelfTypeTempChangeCollectionFromJSON,
  GetShelfTypeTempChangeCollectionsRequest,
  GetShelfTypeTempChangeCollectionsRequestToJSON,
  GetChildCollectionRequest,
  ChildCollection,
  GetChildCollectionRequestToJSON,
  ChildCollectionFromJSON,
  SimpleUpdateAcceptCollectionsRequestToJSON,
  SimpleUpdateAcceptCollectionsRequest,
  GetSimpleAcceptCollectionsRequest,
  GetSimpleAcceptCollectionsRequestToJSON,
  SimpleAcceptCollection,
  SimpleAcceptCollectionFromJSON,
  GetShelfTypeConversionInfoRequest,
  ShelfTypeConversionInfo,
  GetShelfTypeConversionInfoRequestToJSON,
  ShelfTypeConversionInfoFromJSON,
  UpdateCollectionIcTagUidRequest,
  UpdateCollectionIcTagUidRequestToJSON
} from '@/api'
import { BibliographyFullId } from '@/types'
import {
  ReceiptReserve,
  ReceiptReserveFromJSON
} from '@/api/models/receipt/ReceiptReserve'
import * as runtime from '../runtime'

export interface GetCollectionMemoRequest {
  collectionId: number
}

export class CollectionApi extends runtime.BaseAPI {
  /**
   *  利用者IDにより資料メモを取得する
   *  資料メモ取得
   */
  async getCollectionMemoById(
    request: GetCollectionMemoRequest
  ): Promise<CollectionMemo> {
    const response = await this.requestGet(
      `/collections/${request.collectionId}/memo`
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      CollectionMemoFromJSON(jsonValue)
    ).value()
  }

  /**
   * 入力値により資料メモを更新する
   * 資料メモ更新
   */
  async updateCollectionMemo(
    requestBody: UpdateCollectionMemoRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: HTTPBody = UpdateCollectionMemoRequestToJSON(
      requestBody
    )

    const response = await this.requestPut(
      `/collections/${requestBody.id}/memo`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 資料IDにより、資料返却履歴を取得する
   * 資料返却履歴取得
   */
  async getReturnHistories(id: number): Promise<ReturnHistory[]> {
    const response = await this.requestGet(
      `/collections/${id}/return-histories`
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReturnHistoryFromJSON)
    ).value()
  }

  /**
   * 資料を取得する
   * 資料取得
   */
  async getCollection(id: number): Promise<Collection> {
    const response = await this.requestGet(`/collections/${id}`)

    return new runtime.JSONApiResponse(response, jsonValue =>
      CollectionFromJSON(jsonValue)
    ).value()
  }

  /**
   * 資料一括更新をチェックする
   * 資料一括更新チェック
   */
  async checkBulkUpdateCollections(
    request: CheckBulkUpdateCollectionsRequest
  ): Promise<BulkUpdateCollection[]> {
    const bodyParameters: HTTPBody = CheckBulkUpdateCollectionsRequestToJSON(
      request
    )

    const response = await this.requestPost(
      '/collections/bulk-update-check',
      bodyParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(BulkUpdateCollectionFromJSON)
    ).value()
  }

  /**
   * 入力値により資料情報を更新する
   */
  async updateCollection(
    request: UpdateCollectionRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: HTTPBody = UpdateCollectionRequestToJSON(request)

    const response = await this.requestPut(
      `/collections/${request.id}`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 資料を一括更新する
   * 資料一括更新
   */
  async bulkUpdateCollections(
    request: BulkUpdateCollectionsRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: HTTPBody = BulkUpdateCollectionsRequestToJSON(request)

    const response = await this.requestPost(
      '/collections/bulk-update',
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 入力値により本受入資料を取得する
   * 本受入情報取得
   */
  async getOfficialAcceptCollection(
    request: GetOfficialAcceptCollectionRequest
  ): Promise<OfficialAcceptCollection> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(GetOfficialAcceptCollectionRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      '/collections/official-accept',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      OfficialAcceptCollectionFromJSON(jsonValue)
    ).value()
  }

  /**
   * 資料を本受け入れる
   * 本資料本受入
   */
  async acceptOfficialAcceptCollection(
    request: AcceptOfficialAcceptCollectionRequest
  ): Promise<ReceiptReserve[]> {
    const bodyParameters: HTTPBody = AcceptOfficialAcceptCollectionRequestToJSON(
      request
    )
    const response = await this.requestPost(
      `/collections/official-accept`,
      bodyParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ReceiptReserveFromJSON)
    ).value()
  }

  /**
   * 資料番号により書誌番号を取得する
   * 書誌番号取得
   */
  async getBibliographyFullId(
    request: GetBibliographyFullIdRequest
  ): Promise<BibliographyFullId> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(GetBibliographyFullIdRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      `/collections/bibliography-id`,
      queryParameters
    )
    return new runtime.JSONApiResponse(response, jsonValue => {
      return {
        prefix: exists(jsonValue, 'prefix') ? jsonValue.prefix : '',
        id: exists(jsonValue, 'bibliography_id') ? jsonValue.bibliography_id : 0
      }
    }).value()
  }

  /**
   * 資料番号により資料簡易情報を取得する
   * 資料簡易情報取得
   */
  async getSimpleCollections(
    request: GetSimpleCollectionRequest
  ): Promise<SimpleCollection[]> {
    const queryParameter: HTTPQuery = Object.fromEntries(
      Object.entries(GetSimpleCollectionRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      '/collections/simple',
      queryParameter
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(SimpleCollectionFromJSON)
    ).value()
  }

  /**
   * 資料IDまたは資料番号から資料の貸出回数を取得する
   * 貸出回数取得
   */
  async getCollectionLendCount(
    request: GetCollectionLendCountRequest
  ): Promise<CollectionLendCount> {
    const queryParameter: HTTPQuery = Object.fromEntries(
      Object.entries(GetCollectionLendCountRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestPut(
      '/collections/lend-count',
      queryParameter
    )

    return new runtime.JSONApiResponse(
      response,
      CollectionLendCountFromJSON
    ).value()
  }

  /**
   * 資料の除籍を行う
   * 資料除籍
   */
  async collectionRemove(
    request: CollectionRemoveRequest
  ): Promise<runtime.VoidApiResponse> {
    const queryParameter: HTTPQuery = Object.fromEntries(
      Object.entries(CollectionRemoveRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestPut(
      '/collections/remove',
      queryParameter
    )
    return new runtime.VoidApiResponse(response)
  }

  /**
   * 本受入資料をチェックする
   * 本受入資料チェック
   */
  async checkAcceptCollection(
    request: CheckOfficialAcceptCollectionRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: HTTPBody = CheckOfficialAcceptCollectionRequestToJSON(
      request
    ) as HTTPBody

    const response = await this.requestPost(
      `/collections/${bodyParameters.id}/official-accept-check`,
      bodyParameters
    )
    return new runtime.VoidApiResponse(response)
  }

  /**
   * 資料の削除を行う
   * 資料削除
   */
  async collectionDelete(
    request: CollectionDeleteRequest
  ): Promise<runtime.VoidApiResponse> {
    const queryParameter: HTTPQuery = Object.fromEntries(
      Object.entries(CollectionDeleteRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestDelete('/collections', queryParameter)

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 製本資料を取得する
   * 製本資料取得
   */
  async getBookbindingCollection(
    request: GetBookbindingCollectionRequest
  ): Promise<BookbindingCollection> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(GetBookbindingCollectionRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      '/collections/bookbinding',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      BookbindingCollectionFromJSON(jsonValue)
    ).value()
  }

  /**
   * 製本済資料一覧を取得する
   * 製本済資料一覧取得
   */
  async getBookBoundCollections(
    request: GetBookBoundCollectionsRequest
  ): Promise<BookbindingTargetCollection[]> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(GetBookBoundCollectionsRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      '/collections/bound',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(BookbindingTargetCollectionFromJSON)
    ).value()
  }

  /**
   * 製本対象資料一覧を取得する
   * 製本対象資料一覧取得
   */
  async getBookbindingTargetCollections(
    request: GetBookbindingTargetCollectionsRequest
  ): Promise<BookbindingTargetCollection[]> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(
        GetBookbindingTargetCollectionsRequestToJSON(request)
      ).filter(value => value[1] !== undefined)
    ) as HTTPQuery

    const response = await this.requestGet(
      '/collections/bookbinding-target',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(BookbindingTargetCollectionFromJSON)
    ).value()
  }

  /**
   * 資料を製本する
   * 資料製本
   */
  async bookbindingCollections(
    request: BookbindingCollectionsRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: HTTPBody = BookbindingCollectionsRequestToJSON(
      request
    ) as HTTPBody

    const response = await this.requestPost(
      '/collections/bookbinding',
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 製本対象雑誌資料を取得する
   * 製本対象雑誌資料取得
   */
  async getBookbindingTargetMagazineCollections(
    request: GetBookbindingTargetMagazineCollectionsRequest
  ): Promise<BookbindingTargetMagazineCollection[]> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(
        GetBookbindingTargetMagazineCollectionsRequestToJSON(request)
      ).filter(value => value[1] !== undefined)
    ) as HTTPQuery

    const response = await this.requestGet(
      '/collections/bookbinding-target-magazine',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(BookbindingTargetMagazineCollectionFromJSON)
    ).value()
  }

  /**
   * 配架区分一時変更資料を取得する
   * 配架区分一時変更資料取得
   */
  async getShelfTypeTempChangeCollection(
    request: GetShelfTypeTempChangeCollectionRequest
  ): Promise<ShelfTypeTempChangeCollection> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(
        GetShelfTypeTempChangeCollectionRequestToJSON(request)
      ).filter(value => value[1] !== undefined)
    ) as HTTPQuery

    const response = await this.requestGet(
      '/collections/shelf-type-temp-change',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      ShelfTypeTempChangeCollectionFromJSON(jsonValue)
    ).value()
  }

  /**
   * 配架区分を一時変更する
   * 配架区分一時変更
   */
  async changeShelfTypeTemp(
    request: ChangeShelfTypeTempRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: HTTPBody = ChangeShelfTypeTempRequestToJSON(
      request
    ) as HTTPBody

    const response = await this.requestPost(
      '/collections/shelf-type-temp-change',
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 配架区分一時変更資料一覧を取得する
   * 配架区分一時変更資料一覧取得
   */
  async getShelfTypeTempChangeCollections(
    request: GetShelfTypeTempChangeCollectionsRequest
  ): Promise<ShelfTypeTempChangeCollection[]> {
    const bodyParameters: HTTPBody = GetShelfTypeTempChangeCollectionsRequestToJSON(
      request
    )

    const response = await this.requestPost(
      '/collections/shelf-type-temp-change-list',
      bodyParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ShelfTypeTempChangeCollectionFromJSON)
    ).value()
  }

  /**
   * 配架区分一時変更資料を取得する
   * 配架区分一時変更資料取得
   */
  async getShelfTypeTempChangeFixCollections(
    request: GetShelfTypeTempChangeFixCollectionsRequest
  ): Promise<ShelfTypeTempChangeCollection[]> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(
        GetShelfTypeTempChangeFixCollectionsRequestToJSON(request)
      ).filter(value => value[1] !== undefined)
    ) as HTTPQuery

    const response = await this.requestGet(
      '/collections/shelf-type-temp-change-fix',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ShelfTypeTempChangeCollectionFromJSON)
    ).value()
  }

  /**
   * 配架区分を一時変更する
   * 配架区分一時変更
   */
  async fixShelfTypeChange(
    request: FixShelfTypeTempChangeRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: HTTPBody = FixShelfTypeTempChangeRequestToJSON(
      request
    ) as HTTPBody

    const response = await this.requestPost(
      '/collections/shelf-type-temp-change-fix',
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 子資料一覧を取得する
   * 子資料一覧取得
   */
  async getChildCollections(
    request: GetChildCollectionRequest
  ): Promise<ChildCollection[]> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(GetChildCollectionRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      '/collections/child',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(ChildCollectionFromJSON)
    ).value()
  }

  /**
   * 受入資料を簡易的に更新する
   * 受入資料簡易更新
   */
  async simpleUpdateAcceptCollections(
    request: SimpleUpdateAcceptCollectionsRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: HTTPBody = SimpleUpdateAcceptCollectionsRequestToJSON(
      request
    )

    const response = await this.requestPut(
      '/collections/simple-accept',
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }

  /**
   * 簡易受入資料を取得
   * 簡易受入資料取得
   */
  async getSimpleAcceptCollections(
    request: GetSimpleAcceptCollectionsRequest
  ): Promise<SimpleAcceptCollection[]> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(GetSimpleAcceptCollectionsRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      '/collections/simple-accept',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      jsonValue.map(SimpleAcceptCollectionFromJSON)
    ).value()
  }

  /**
   * 配架区分の変換情報を取得
   * 配架区分変換情報取得
   */
  async getShelfTypeConversionInfo(
    request: GetShelfTypeConversionInfoRequest
  ): Promise<ShelfTypeConversionInfo> {
    const queryParameters: HTTPQuery = Object.fromEntries(
      Object.entries(GetShelfTypeConversionInfoRequestToJSON(request)).filter(
        value => value[1] !== undefined
      )
    ) as HTTPQuery

    const response = await this.requestGet(
      '/collections/shelf-type-conversion',
      queryParameters
    )

    return new runtime.JSONApiResponse(response, jsonValue =>
      ShelfTypeConversionInfoFromJSON(jsonValue)
    ).value()
  }

  /**
   * 資料のICタグUIDを更新する
   * 資料ICタグUID更新
   */
  async updateCollectionIcTagUid(
    request: UpdateCollectionIcTagUidRequest
  ): Promise<runtime.VoidApiResponse> {
    const bodyParameters: HTTPBody = UpdateCollectionIcTagUidRequestToJSON(
      request
    )

    const response = await this.requestPut(
      `/collections/${bodyParameters.id}/ic-tag-uid`,
      bodyParameters
    )

    return new runtime.VoidApiResponse(response)
  }
}
